import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { ArrowLeft, ChevronRight, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import usePageParam from "../../hooks/usePageParam";
import { INVESTING_SLUG_RE, investingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import ThesisStatusDialog from "./ThesisStatusDialog";
import UntrustedMarkdown from "./UntrustedMarkdown";
import VerdictBadge from "./VerdictBadge";
import { companyPath, discussionPath, formatDay, formatScore, scoreTone, whoName } from "./lib/format";
import { revisionChanges, revisionHeadline, revisionValue } from "./lib/thesisRevision";

// The API's page size for companies (constants.py COMPANY_PAGE_SIZE).
const PAGE_SIZE = 100;
const SLUG_MAX = 60;

const PARTS = [
  ["claim", "Claim", "The one statement it makes."],
  ["mechanism", "Mechanism", "Why it would be true."],
  ["winners_losers", "Winners and losers", "Who wins and who loses."],
  ["falsifier", "Falsifier", "What would prove it wrong."],
];

/** The argument: claim, mechanism, winners and losers, falsifier — agent-written markdown, untrusted (D8). */
function Argument({ thesis }) {
  return (
    <section aria-labelledby="thesis-argument" className="rounded-lg border bg-card p-4">
      <h2 id="thesis-argument" className="mt-0 mb-3 text-lg font-semibold">
        The argument
      </h2>
      <div className="flex flex-col gap-4">
        {PARTS.map(([key, label, hint]) => (
          <div key={key}>
            <h3 className="m-0 text-base font-semibold">{label}</h3>
            <p className="mt-0 mb-1 text-xs text-muted-foreground">{hint}</p>
            {thesis[key] ? (
              <div className="text-sm">
                <UntrustedMarkdown body={thesis[key]} />
              </div>
            ) : (
              <p className="m-0 text-sm text-muted-foreground">Not written yet.</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

Argument.propTypes = {
  thesis: PropTypes.shape({
    claim: PropTypes.string,
    mechanism: PropTypes.string,
    winners_losers: PropTypes.string,
    falsifier: PropTypes.string,
  }).isRequired,
};

/** This thesis's score on a row of the companies list (the row lists every thesis's). */
function scoreOn(row, slug) {
  return (Array.isArray(row.scores) ? row.scores : []).find((s) => s.thesis === slug)?.score ?? null;
}

/**
 * The companies the thesis scores, from the companies list (thesis=<slug>,
 * ordered by its score, highest first), 100 a page with the page in the
 * URL. A page past the end heals to page 1; only the newest page lands.
 */
function ThesisCompanies({ slug }) {
  const [page, setPage] = usePageParam();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const requestRef = useRef(0);
  useEffect(() => {
    const request = ++requestRef.current;
    const isLatest = () => request === requestRef.current;
    setLoading(true);
    setError(null);
    investingApi.companies.list({ thesis: slug, ordering: `-score:${slug}`, page }).then(
      ({ data }) => {
        if (!isLatest()) return;
        setRows(data?.results || []);
        setCount(data?.count || 0);
        setLoading(false);
      },
      (err) => {
        if (!isLatest()) return;
        if (err?.response?.status === 404 && page > 1) {
          setPage(1, { replace: true });
          return;
        }
        setError(err);
        setLoading(false);
      },
    );
  }, [slug, page, setPage, reloadTick]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const first = (page - 1) * PAGE_SIZE + 1;
  const last = Math.min(page * PAGE_SIZE, count);
  const summary =
    `${count.toLocaleString("en-US")} ${count === 1 ? "company" : "companies"} scored, highest score first` +
    (totalPages > 1 ? ` — page ${page} of ${totalPages}, showing ${first}–${last}.` : ".");

  return (
    <section aria-labelledby="thesis-companies" className="rounded-lg border bg-card p-4">
      <h2 id="thesis-companies" className="mt-0 mb-1 text-lg font-semibold">
        Companies
      </h2>
      <p role="status" className="mt-0 mb-3 text-sm text-muted-foreground">
        {loading ? "Loading its companies…" : error || count === 0 ? "" : summary}
      </p>
      {error ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription className="flex flex-wrap items-center gap-2">
            <span>{apiErrorMessage(error, "Couldn't load its companies.")}</span>{" "}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 md:h-8"
              onClick={() => setReloadTick((tick) => tick + 1)}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : loading && rows.length === 0 ? (
        <div className="py-6 text-center">
          <Loader2 className="inline size-5 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      ) : !loading && count === 0 ? (
        <p className="m-0 text-sm">
          No company is scored against it yet. Research scores each company against the theses that apply, from −2
          to +2; a sweep task scores the companies a thesis names.
        </p>
      ) : (
        <ul
          aria-label={`Companies scored on ${slug}`}
          aria-busy={loading || undefined}
          className={cn("m-0 flex list-none flex-col p-0", loading && "opacity-60")}
        >
          {rows.map((row) => {
            const score = scoreOn(row, slug);
            return (
              <li key={row.symbol} className="flex items-center gap-3 border-b border-border py-1.5 last:border-b-0">
                <span className={cn("w-7 shrink-0 text-right font-semibold tabular-nums", scoreTone(score))}>
                  {score === null ? "–" : formatScore(score)}
                </span>{" "}
                <span className="min-w-0 flex-1 text-sm">
                  <Link
                    to={companyPath(row.symbol)}
                    className="inline-flex min-h-11 min-w-11 items-center font-semibold underline-offset-2 hover:underline md:min-h-0 md:min-w-0"
                  >
                    {row.symbol}
                  </Link>{" "}
                  {/* Under the symbol on a phone, beside it from md. */}
                  <span className="block text-muted-foreground wrap-anywhere md:inline">
                    {row.name}
                    {row.sector ? ` · ${row.sector}` : ""}
                  </span>
                </span>{" "}
                <VerdictBadge verdict={row.verdict} conviction={row.conviction} className="shrink-0" />
              </li>
            );
          })}
        </ul>
      )}
      {!error && totalPages > 1 && (
        <Pagination className="mt-4" page={page} total={totalPages} onChange={(next) => setPage(next)} showEdges />
      )}
    </section>
  );
}

ThesisCompanies.propTypes = {
  slug: PropTypes.string.isRequired,
};

/** One value of a revision, as plain text — never markdown or HTML. */
function RevisionText({ value }) {
  return (
    <p className="m-0 max-h-48 overflow-y-auto rounded-md border border-border bg-muted/40 px-2 py-1 text-sm whitespace-pre-wrap wrap-anywhere">
      {value === null ? "(none)" : value === "" ? "(empty)" : value}
    </p>
  );
}

RevisionText.propTypes = {
  value: PropTypes.string,
};

/** The newest revisions, with how many there are in all. */
function RevisionHistory({ revisions, total }) {
  const listed = revisions.length;
  let lead;
  if (total === 0) lead = "No revisions recorded.";
  else if (listed < total) lead = `The newest ${listed} of ${total} revisions, newest first.`;
  else lead = `${total} ${total === 1 ? "revision" : "revisions"}.`;
  return (
    <section aria-labelledby="thesis-history" className="rounded-lg border bg-card p-4">
      <h2 id="thesis-history" className="mt-0 mb-1 text-lg font-semibold">
        Revision history
      </h2>
      <p className="mt-0 mb-3 text-sm text-muted-foreground">
        {lead} Every change is kept with who made it and why.
      </p>
      {listed > 0 && (
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {revisions.map((revision) => {
            const changes = revisionChanges(revision);
            return (
              <li key={revision.id} className="rounded-md border border-border px-3 py-2 text-sm">
                <p className="m-0 font-medium">{revisionHeadline(revision)}</p>
                <p className="m-0 wrap-anywhere">{revision.reason}</p>
                <p className="m-0 text-muted-foreground">
                  {whoName(revision.by)}, {formatDay(revision.at)}
                </p>
                {changes.length > 0 && (
                  <details className="group mt-1">
                    <summary className="flex min-h-11 cursor-pointer items-center gap-1 text-sm md:min-h-8">
                      <ChevronRight
                        aria-hidden="true"
                        className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
                      />
                      What changed
                    </summary>
                    <div className="mt-1 flex flex-col gap-2">
                      {changes.map((change) => (
                        <div key={change.field} className="flex flex-col gap-1">
                          <p className="m-0 font-medium">{change.label}</p>
                          {change.before !== null && (
                            <>
                              <p className="m-0 text-xs text-muted-foreground">Before</p>
                              <RevisionText
                                value={revisionValue(change.field, change.before) ?? (change.before === "" ? "" : null)}
                              />
                            </>
                          )}
                          <p className="m-0 text-xs text-muted-foreground">After</p>
                          <RevisionText
                            value={revisionValue(change.field, change.after) ?? (change.after === "" ? "" : null)}
                          />
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {listed < total && (
        <p className="mt-2 mb-0 text-xs text-muted-foreground">
          The page lists the newest {listed}; the {total - listed} older ones are kept in the record.
        </p>
      )}
    </section>
  );
}

RevisionHistory.propTypes = {
  revisions: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.number.isRequired,
};

/**
 * A thesis's page (spec §5.3, walkthrough 5), at /investing/theses/:slug:
 * its argument — claim, mechanism, winners and losers, falsifier, as
 * agent-written markdown rendered untrusted (D8) — the companies it scores
 * (from the companies list, highest score first, 100 a page), and its
 * revision history (the newest 20 with how many there are).
 *
 * Retire / Revive is a person's (spec §2.1): it shows only once `me` says
 * human — never while unknown, never to an agent — and the server's 403
 * is still shown as it says.
 */
function InvestingThesis() {
  const { slug = "" } = useParams();
  const { me } = useOutletContext() || {};
  const isPerson = me?.kind === "human";
  const valid = slug.length <= SLUG_MAX && INVESTING_SLUG_RE.test(slug);

  const [thesis, setThesis] = useState(null);
  const [failure, setFailure] = useState(null); // {notFound} | {message}
  const [reloadTick, setReloadTick] = useState(0);
  const [status, setStatus] = useState("");
  // Retire / Revive. The dialog hands focus back to its opener (ui/dialog):
  // the same button, now saying Revive (or Retire).
  const [statusOpen, setStatusOpen] = useState(false);

  const requestRef = useRef(0);
  // The thesis on screen now, for a save that lands after the person moved
  // to another one (Back, a link): it must not replace that one.
  const slugRef = useRef(slug);
  slugRef.current = slug;
  useEffect(() => {
    setThesis(null);
    setFailure(null);
    setStatus("");
    setStatusOpen(false);
  }, [slug]);

  useEffect(() => {
    if (!valid) return;
    const request = ++requestRef.current;
    investingApi.theses.get(slug).then(
      ({ data }) => {
        if (request !== requestRef.current) return;
        setThesis(data);
        setFailure(null);
      },
      (err) => {
        if (request !== requestRef.current) return;
        setFailure(
          err?.response?.status === 404
            ? { notFound: true }
            : { message: apiErrorMessage(err, "Couldn't load this thesis.") },
        );
      },
    );
  }, [slug, valid, reloadTick]);

  // --- Retire / Revive ---------------------------------------------------
  const statusSaved = useCallback((data) => {
    if (data?.slug !== slugRef.current) return;
    setThesis(data);
    setStatusOpen(false);
    setStatus(
      data.status === "retired"
        ? "Retired — it takes no new scores. Your reason is in its revision history."
        : "Revived — it takes new scores again. Your reason is in its revision history.",
    );
  }, []);

  const back = (
    <Link
      to="/investing/theses"
      aria-label="Back to Theses"
      className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground hover:text-foreground md:min-h-8"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      Theses
    </Link>
  );

  if (!valid || failure?.notFound) {
    return (
      <div className="page-container mt-6 pt-4">
        {back}
        <div className="rounded-lg border bg-card p-4">
          <p className="m-0">
            {valid
              ? `${slug} isn't one of your theses. Theses arrive when an agent works a board task “Investing: thesis — <name>”.`
              : `“${slug}” isn't a thesis slug — slugs are lowercase words joined by hyphens, like labour-sellers.`}
          </p>
        </div>
      </div>
    );
  }

  if (!thesis) {
    return (
      <div className="page-container mt-6 pt-4">
        {back}
        {failure ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription className="flex flex-wrap items-center gap-2">
              <span>{failure.message}</span>{" "}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 md:h-8"
                onClick={() => setReloadTick((tick) => tick + 1)}
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="py-12 text-center" role="status">
            <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="sr-only">Loading {slug}…</span>
          </div>
        )}
      </div>
    );
  }

  const retired = thesis.status === "retired";
  const created = formatDay(thesis.created_at);
  const changed = formatDay(thesis.updated_at);
  const meta = [
    thesis.slug,
    `Written by ${whoName(thesis.created_by)}${created ? ` on ${created}` : ""}${
      changed && changed !== created ? ` · changed ${changed}` : ""
    }`,
  ].join(" · ");
  const revisions = Array.isArray(thesis.revisions) ? thesis.revisions : [];

  return (
    <div className="page-container mt-6 pt-4">
      <div className="max-w-4xl">
        {back}
        <header className="mb-4">
          <h1 className="mb-1 font-display text-3xl font-semibold tracking-tight wrap-anywhere">
            {thesis.title}
            {retired && (
              <>
                {" "}
                <Badge variant="outline" className="align-middle font-sans text-sm">
                  Retired
                </Badge>
              </>
            )}
          </h1>
          <p className="m-0 text-sm wrap-anywhere text-muted-foreground">{meta}</p>
          {thesis.discussion && (
            <p className="m-0 text-sm">
              Discussion:{" "}
              <Link
                to={discussionPath(thesis.discussion.id)}
                className="inline-flex min-h-11 items-center underline underline-offset-2 wrap-anywhere md:min-h-0"
              >
                {thesis.discussion.title}
              </Link>
            </p>
          )}
          {retired && (
            <p className="mt-2 mb-0 rounded-md border border-border bg-muted/40 p-3 text-sm">
              Retired — it takes no new scores, and it can&apos;t be a new Own verdict&apos;s primary thesis. The
              scores it gave stay on the companies&apos; pages.
            </p>
          )}
          {isPerson && (
            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                className="h-11 md:h-9"
                onClick={() => setStatusOpen(true)}
              >
                {retired ? "Revive thesis" : "Retire thesis"}
              </Button>
            </div>
          )}
        </header>

        <p role="status" className={cn("text-sm", status ? "mb-4" : "m-0")}>
          {status}
        </p>

        <div className="flex flex-col gap-4">
          <Argument thesis={thesis} />
          <ThesisCompanies slug={thesis.slug} />
          <RevisionHistory revisions={revisions} total={thesis.revisions_total ?? revisions.length} />
        </div>
      </div>

      {isPerson && (
        <ThesisStatusDialog
          open={statusOpen}
          thesis={{ slug: thesis.slug, title: thesis.title, status: thesis.status }}
          onOpenChange={setStatusOpen}
          onSaved={statusSaved}
        />
      )}
    </div>
  );
}

export default InvestingThesis;
